import React from 'react';
import { Card } from './ui/card';
import { Check, X } from 'lucide-react';

export const Amenities = () => {
  const amenityCategories = [
    {
      title: 'Modern Living Spaces',
      items: [
        'Floor-to-ceiling windows with natural light',
        'Spacious patio with outdoor seating',
        'Scandinavian oak wood throughout',
        'Climate control (Heat + Cool)',
        'Modern furniture and design',
        'Art and curated decor'
      ]
    },
    {
      title: 'Kitchen & Dining',
      items: [
        'Fully equipped modern kitchen',
        'Italian Coffee maker and kettle',
        'Premium German appliances',
        'Dishwasher, Fridge, Freezer',
        'Cookware & utensils',
        'Dining table seats 4'
      ]
    },
    {
      title: 'Technology & Connectivity',
      items: [
        'Modern Smart TV with streaming',
        '10GB fiber internet connection',
        'USB charging ports & adapters',
        'Work-friendly desk space',
        'Bluetooth sound system',
        'Dimmable smart LEDs'
      ]
    },
    {
      title: 'Comfort & Convenience',
      items: [
        'Premium linens and towels',
        'Extra pillows and blankets',
        'Iron, hair dryer, toiletries',
        'Washer / Dryer combo',
        'Euro King bed sleeps 2',
        '2 Convertible daybeds (sleeps up to 4)'
      ]
    },
    {
      title: 'Security & Privacy',
      items: [
        { text: 'External camera and motion sensing light (front entrance only)', isNegative: false },
        { text: 'Large SAFE available (fits laptops and purses)', isNegative: false },
        { text: 'Automatically locking door', isNegative: false },
        { text: 'Private secure entrance', isNegative: false },
        { text: 'Doorbell with intercom', isNegative: false },
        { text: 'No interior cameras', isNegative: true }
      ]
    },
    {
      title: 'Entertainment & Relaxation',
      items: [
        'Dual-pane windows & insulated walls',
        'Well-stocked library & boardgames',
        'Smart TV with streaming services',
        'Hammock loungers and daybeds',
        'Bluetooth music sound system',
        'Spa style modern shower'
      ]
    }
  ];
  
  return (
    <section className="py-16 sm:py-20 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Compact Section Header */}
        <div className="max-w-3xl mb-10">
          <h2 className="text-3xl sm:text-4xl font-display font-semibold text-foreground mb-3">
            Everything You Need
          </h2>
          <p className="text-base text-muted-foreground leading-relaxed">
            Modern amenities and thoughtful touches to make your stay effortless. Perfect for families, digital nomads, and discerning travelers. Expect an experience like no other and an oasis to balance out your exciting adventures nearby.
          </p>
        </div>
        
        {/* Compact Amenities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {amenityCategories.map((category, index) => (
            <Card key={index} className="p-6 bg-card border-border">
              <h3 className="text-lg font-display font-semibold text-foreground mb-4">
                {category.title}
              </h3>
              <ul className="space-y-2.5">
                {category.items.map((item, itemIndex) => {
                  const isObject = typeof item === 'object';
                  const text = isObject ? item.text : item;
                  const isNegative = isObject ? item.isNegative : false;
                  
                  return (
                    <li key={itemIndex} className="flex items-start gap-2.5">
                      <div className="flex-shrink-0 mt-0.5">
                        {isNegative ? (
                          <div className="w-4 h-4 rounded-full bg-red-500/10 flex items-center justify-center">
                            <X size={12} className="text-red-500" strokeWidth={3} />
                          </div>
                        ) : (
                          <div className="w-4 h-4 rounded-full bg-primary/10 flex items-center justify-center">
                            <Check size={12} className="text-primary" strokeWidth={3} />
                          </div>
                        )}
                      </div>
                      <span className="text-sm text-muted-foreground leading-relaxed">
                        {text}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </Card>
          ))}
        </div>
        
        {/* Compact Ideal For Tags */}
        <div className="mt-8">
          <p className="text-sm text-muted-foreground mb-3">Ideal for:</p>
          <div className="flex flex-wrap gap-2">
            {['Families', 'Remote Workers', 'Digital Nomads', 'Expats', 'Traveling Professionals', 'Long Stays'].map((tag, index) => (
              <div 
                key={index}
                className="px-3 py-1.5 rounded-tl-md rounded-br-md rounded-tr-none rounded-bl-none bg-muted/50 text-xs font-medium text-foreground"
              >
                {tag}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};